import 'babel-polyfill';
import 'moment-duration-format';
import 'transitionEnd';
import isFinite from 'lodash/isFinite';
import isNull from 'lodash/isNull';
import isUndefined from 'lodash/isUndefined';
import merge from 'lodash/merge';
import {AppContainer} from 'react-hot-loader';
import React from 'react';
import {render} from 'react-dom';
import moment from 'moment';
import cloudinary from 'cloudinary-core';
import Sentry from 'sentry';
import SegmentUtils from 'utils/segment';
import StorageUtils from 'storage-utils';
import SHGlobals from 'sh-globals';
import DateUtils from 'date-utils';
import ConfigAPI from './api/config';
import EventsAPI from './api/events';
import store from './store';
import Root from './Root';

const getMediaSource = () => {
    if (document.referrer.search('https?://(.*)google.([^/?]*)') === 0) {
        return 'Google';
    } else if (document.referrer.search('https?://(.*)bing.([^/?]*)') === 0) {
        return 'Bing';
    } else if (document.referrer.search('https?://(.*)yahoo.([^/?]*)') === 0) {
        return 'Yahoo';
    } else if (document.referrer.search('https?://(.*)facebook.([^/?]*)') === 0) {
        return 'Facebook';
    } else if (document.referrer.search('https?://(.*)twitter.([^/?]*)') === 0) {
        return 'Twitter';
    } else {
        return 'Other';
    }
};

const segmentInitOnLoad = () => {
    if (window.spotHeroUser) {
        const segmentTraits = {
            'media source': getMediaSource(),
            email: window.spotHeroUser.email,
            'user id': window.spotHeroUser.id,
            created: window.spotHeroUser.created,
            name: window.spotHeroUser.full_name,
            'new user': window.spotHeroUser.isNewUser,
            'referrer code': window.spotHeroUser.referralToken
        };

        SegmentUtils.identify({
            uniqueId: window.spotHeroUser.id,
            traits: segmentTraits
        });
    }
};

const renderWidget = ({initData, settings}) => {
    const widgetStore = store(initData);

    render((
        <AppContainer>
            <Root
                store={widgetStore}
                settings={settings}
            />
        </AppContainer>
    ), settings.container);

    if (module.hot) {
        module.hot.accept('./Root', () => {
            const RootContainer = require('./Root').default;

            render((
                <AppContainer>
                    <RootContainer
                        store={widgetStore}
                        settings={settings}
                    />
                </AppContainer>
            ), settings.container);
        });
    }

    window.shTestInfo.widgetOpened = true;
};

const WidgetCommon = {
    loadConfig(cb) {
        ConfigAPI
            .get()
            .then(({data}) => {
                cb(data.data);
            })
            .catch(resp => {
                // provide fallbacks in case the config can't be loaded for any reason so that the widget doesn't just fail
                cb({
                    staticfiles_domain: 'd2yt77z1f5bzb8.cloudfront.net',
                    spothero_stripe_public_key: 'pk_eUzGsVbITKKQb5dfSDHxs13a2Vg5D',
                    cloudinary_cloud_name: 'spothero',
                    sentry_api_public_key: 'https://9010d263b04a460fb5aea0beae3d0eff@sentry.io/97150',
                    site_url_secure: 'https://spothero.com',
                    segment_frontend_enabled: true,
                    consumer_sales_funnel_segment_token: 'BWFrP9PEt74cnJQMV6TQVYLFkOJpHdb8',
                    ua_tracking_id: 'GTM-5VQ7Z4',
                    is_deployed: true,
                    is_production: true
                });
            });
    },

    setup(settings, isScript = false) {
        const cl = cloudinary.Cloudinary.new({cloud_name: settings.cloudinaryCloudName});

        window.currentCityTimezone = DateUtils.getTimeZone();

        settings.showDefaultSubtitle = (settings.showDefaultSubtitle) ? true : false;
        settings.unbranded = (settings.unbranded) ? true : false;
        settings.monthly = (settings.monthly) ? true : false;
        settings.hourSpan = settings.hourSpan || 3;

        if (!settings.startDate || !settings.startTime) {
            const now = moment();
            now.add((30 - now.minute()) % 30, 'minutes');

            settings.startDate = now.format(SHGlobals.URL_DATE_FORMAT);
            settings.startTime = now.format(SHGlobals.DESKTOP_TIME_FORMAT);
        }

        if (!settings.endDate || !settings.endTime) {
            const endDateTime = moment(`${settings.startDate} ${settings.startTime}`, `${SHGlobals.URL_DATE_FORMAT} ${SHGlobals.DESKTOP_TIME_FORMAT}`)
                .add(settings.hourSpan, 'hours');

            settings.endDate = endDateTime.format(SHGlobals.URL_DATE_FORMAT);
            settings.endTime = endDateTime.format(SHGlobals.DESKTOP_TIME_FORMAT);
        }

        settings.hourSpan = parseInt(settings.hourSpan, 10);
        settings.width = parseInt(settings.width, 10);
        settings.height = parseInt(settings.height, 10);

        if (!isFinite(settings.zoom)) {
            settings.zoom = 15;
        }

        const params = {
            starts: DateUtils.combineDateAndTime(
                moment(settings.startDate, [SHGlobals.URL_DATE_FORMAT]),
                moment(settings.startTime, [SHGlobals.DESKTOP_TIME_FORMAT])
            ).format(SHGlobals.SERVER_DATE_TIME_FORMAT),
            ends: DateUtils.combineDateAndTime(
                moment(settings.endDate, [SHGlobals.URL_DATE_FORMAT]),
                moment(settings.endTime, [SHGlobals.DESKTOP_TIME_FORMAT])
            ).format(SHGlobals.SERVER_DATE_TIME_FORMAT),
            monthly: settings.monthly
        };

        if (settings.latitude && settings.longitude) {
            params.latitude = parseFloat(settings.latitude);
            params.longitude = parseFloat(settings.longitude);
        }

        if (settings.monthly) {
            params.monthly_start_date = params.starts;

            delete params.starts;
            delete params.ends;
        }

        if (settings.operator) {
            params.operator_id = parseInt(settings.operator, 10);
        }

        if (isScript) {
            const shCSS = document.createElement('link');
            shCSS.rel = 'stylesheet';
            shCSS.media = 'all';
            shCSS.href = (window.location.port === '3000')
                ? '/static/widget/css/widget.css'
                : `//${settings.staticFilesDomain}/static/widget/css/sh.widget.min.css`;

            const fontCSS = document.createElement('link');
            fontCSS.rel = 'stylesheet';
            fontCSS.href = '//fonts.googleapis.com/css?family=Open+Sans:300,400,600';

            document.getElementsByTagName('head')[0].appendChild(shCSS);
            document.getElementsByTagName('head')[0].appendChild(fontCSS);
        }

        settings.params = params;

        SHGlobals.isDeployed = settings.isDeployed;
        SHGlobals.isProduction = settings.isProduction;
        SHGlobals.cloudfrontViewerCountryCode = settings.cloudfrontViewerCountryCode;
        SHGlobals.cloudinary = cl;
        SHGlobals.initPaymentProcessor(settings.stripeKey);

        if (settings.isSegmentEnabled) {
            SegmentUtils.init({
                apiKey: SHGlobals.segmentApiKey,
                onLoad: segmentInitOnLoad,
                debug: !settings.isDeployed
            });
        }

        if (settings.isProduction) {
            Sentry.init({
                apiKey: settings.sentryKey,
                additionalRavenOptions: {
                    ignoreUrls: [
                        /d2yt77z1f5bzb8\.cloudfront\.net\/static\/control_panel\/js/i,
                        /d2yt77z1f5bzb8\.cloudfront\.net\/static\/main\/js/i
                    ]
                }
            });
            Sentry.addData(settings);
        }

        if (!settings.isProduction) {
            window.TestUtils = require('react-dom/test-utils');
        }

        const showSearchPage = (
            settings.searchType !== 'event'
            && isUndefined(settings.latitude)
            && isUndefined(settings.longitude)
        );
        const eventId = settings.eventId || null;
        let initData = {
            search: {
                isPending: false,
                data: {
                    searchType: settings.searchType || 'address',
                    currentPage: (showSearchPage)
                        ? 'search'
                        : (settings.searchType === 'event') ? 'events' : 'map',
                    zoom: settings.zoom,
                    destinationTitle: settings.destinationTitle || 'Destination',
                    venueId: settings.venueId || null,
                    eventId,
                    eventFallback: (settings.eventFallback) ? true : null,
                    isSingleEvent: !isNull(eventId),
                    params: settings.params
                },
                error: null
            }
        };

        if (!isNull(eventId)) {
            EventsAPI
                .getById(eventId)
                .then(({data}) => {
                    const eventData = data.data;
                    const {
                        destination: {
                            latitude,
                            longitude
                        },
                        starts,
                        ends,
                        id: eid
                    } = eventData;

                    settings.params = {
                        ...settings.params,
                        latitude,
                        longitude,
                        starts: moment(starts).format(SHGlobals.SERVER_DATE_TIME_FORMAT),
                        ends: moment(ends).format(SHGlobals.SERVER_DATE_TIME_FORMAT),
                        eid
                    };

                    initData = merge({}, initData, {
                        search: {
                            data: {
                                currentPage: 'map',
                                selectedEvent: eventData,
                                params: {
                                    ...settings.params
                                }
                            }
                        }
                    });

                    renderWidget({
                        initData,
                        settings
                    });
                })
                .catch(resp => {
                    initData = merge({}, initData, {
                        search: {
                            data: {
                                eventId: null,
                                isSingleEvent: false
                            }
                        }
                    });

                    renderWidget({
                        initData,
                        settings
                    });
                });

        } else {
            renderWidget({
                initData,
                settings
            });
        }
    }
};

export default WidgetCommon;



// WEBPACK FOOTER //
// ./widget/src/js/widget-common.js